import type { RenderContext } from './RenderContext';
import { getExpertRelayCaptionLabel } from './expertRelayCaption';
import { isOutgoingMessageProcessingPending } from './processingPending';

import type { ICustomMessage } from '@/components/chat';
import { isMessageFromCurrentUser } from '@/components/chatHelpers';
import { hasApprovedExtractedStatements } from '@/features/reference-assistant/model';
import { hasApprovedExtractedQuestions } from '@/services/stores/extractedQuestionsStore';
import { MESSAGE_DATA_TYPES } from '@/types/messages/discriminators';
import {
  getAnonymousPrimumRoutedRelayExpert,
  isAskAnonymouslyRoutedInquiry,
} from '@/util/inquiryDisplay';

export type MessagePlanContext = Pick<
  RenderContext,
  'userId' | 'inquiry' | 'isAuthor' | 'statementReferences'
>;

/**
 * Every decision about a message that both render levels need to agree on.
 *
 * GiftedChat renders a message at two levels: the row (`renderMessage`) sizes
 * and positions it, then the bubble (`renderBubble`) fills it in. Any rule that
 * spans the two has to be derived once and read twice, or the two drift.
 *
 * Row *sizing* is no longer one of them: the registry declares which bubble
 * renders and whether it wants a full-width row, and the row reads that
 * directly. What stays here are the row facts that are not per-type — caption
 * state, relay chrome, processing, position.
 *
 * This extends the approach `isOutgoingMessageProcessingPending` already takes
 * for the processing rule to the rest of them.
 */
export type MessagePlan = {
  isCurrentUserMessage: boolean;
  position: 'left' | 'right';
  isExpertRelayMessage: boolean;
  /** A caption line sits above the bubble, which changes how the row composes. */
  isCaptionViewer: boolean;
  messageCaption: string;
  showQuestionsBubble: boolean;
  showStatementsBubble: boolean;
  showDefaultBubble: boolean;
  isPendingProcessing: boolean;
};

/**
 * Derives a message's render plan from the message and the shared context.
 *
 * Pure and deterministic, which is what lets both render levels call it
 * independently and still reach the same answer without passing state between
 * them.
 */
export function resolveMessagePlan(message: ICustomMessage, ctx: MessagePlanContext): MessagePlan {
  const isCurrentUserMessage = isMessageFromCurrentUser(message, ctx.userId);

  const isAskAnonymouslyInbox = isAskAnonymouslyRoutedInquiry(ctx.inquiry);

  const isExpertRelayMessage =
    isAskAnonymouslyInbox &&
    (message.contentRole === 'expert_relay' || message.type === MESSAGE_DATA_TYPES.EXPERT_RELAY);

  /**
   * The caption is the Ask Anonymously attribution line, and it only ever hosts
   * a relayed expert reply. The submitter-side "Primum Support" label is owned
   * by the Primum Support *row* entry, which claims those messages before the
   * bubble level runs — so a non-relay message never reaches a caption here.
   * It applies to both anonymous and No Expert conversations in that inbox.
   */
  const isCaptionViewer = isExpertRelayMessage;

  const titleExpert = getAnonymousPrimumRoutedRelayExpert(ctx.inquiry);

  /** A name set on the message itself overrides the inquiry's attributed expert for this one reply. */
  const relayExpertLabel =
    message.expertName ?? (titleExpert ? getExpertRelayCaptionLabel(titleExpert) : null);

  /**
   * The caption only ever labels a relayed expert reply, so it is always the
   * expert attribution. The submitter-side "Primum Support" label belongs to
   * the Primum Support row entry, not to this caption.
   */
  const messageCaption = relayExpertLabel ? `From ${relayExpertLabel}` : 'Expert reply';

  const showQuestionsBubble = hasApprovedExtractedQuestions(message);

  const showStatementsBubble =
    !showQuestionsBubble &&
    hasApprovedExtractedStatements(ctx.statementReferences.get(message.messageId));

  const showDefaultBubble = !showQuestionsBubble && !showStatementsBubble;

  const isPendingProcessing = isOutgoingMessageProcessingPending(message, ctx);

  return {
    isCurrentUserMessage,
    position: isCurrentUserMessage ? 'right' : 'left',
    isExpertRelayMessage,
    isCaptionViewer,
    messageCaption,
    showQuestionsBubble,
    showStatementsBubble,
    showDefaultBubble,
    isPendingProcessing,
  };
}
